import React, { useEffect, useRef, useState } from 'react'
import { createPortal } from 'react-dom'
import StatusProgressIcon from '../StatusProgressIcon/StatusProgressIcon'
import { normalizeApplicationStatusKey } from '../../utils/applicationStatusUi'
import s from './ApplicationStatusPicker.module.scss'
import heroS from './Hero.module.scss'

function ApplicationStatusPicker({
	statusKey,
	statusLabel,
	isOpen,
	onToggle,
	onSelect,
	options = [],
	isSaving = false,
}) {
	const btnRef = useRef(null)
	const menuRef = useRef(null)
	const [menuStyle, setMenuStyle] = useState(null)

	useEffect(() => {
		if (!isOpen) return undefined
		const onDoc = e => {
			if (btnRef.current?.contains(e.target)) return
			if (menuRef.current?.contains(e.target)) return
			onToggle?.()
		}
		document.addEventListener('mousedown', onDoc)
		return () => document.removeEventListener('mousedown', onDoc)
	}, [isOpen, onToggle])

	useEffect(() => {
		if (!isOpen) return undefined
		const updateMenuPosition = () => {
			const btn = btnRef.current
			if (!btn) return
			const rect = btn.getBoundingClientRect()
			const menuMinWidth = 280
			const viewportPadding = 8
			const nextLeft = Math.max(
				viewportPadding,
				Math.min(
					rect.right - menuMinWidth,
					window.innerWidth - menuMinWidth - viewportPadding,
				),
			)
			setMenuStyle({
				position: 'fixed',
				top: rect.bottom + 8,
				left: nextLeft,
				zIndex: 2000,
			})
		}
		updateMenuPosition()
		window.addEventListener('resize', updateMenuPosition)
		window.addEventListener('scroll', updateMenuPosition, true)
		return () => {
			window.removeEventListener('resize', updateMenuPosition)
			window.removeEventListener('scroll', updateMenuPosition, true)
		}
	}, [isOpen])

	const menu =
		isOpen && menuStyle
			? createPortal(
					<div
						ref={menuRef}
						className={s.menu}
						role="listbox"
						style={menuStyle}
						aria-label="Application status"
					>
						{options.map(opt => {
							const selected =
								normalizeApplicationStatusKey(opt.value) ===
								normalizeApplicationStatusKey(statusKey)
							return (
								<button
									key={opt.value}
									type="button"
									className={s.option}
									role="option"
									aria-selected={selected}
									disabled={isSaving}
									onClick={() => onSelect?.(opt.value)}
								>
									<StatusProgressIcon
										status={opt.value}
										className={s.optionIcon}
										size={14}
									/>
									<span className={s.optionLabel}>{opt.menuLabel}</span>
								</button>
							)
						})}
					</div>,
					document.body,
				)
			: null

	return (
		<>
			<button
				ref={btnRef}
				type="button"
				className={[
					heroS.statusBanner,
					heroS.statusBannerBtn,
					heroS[`status_${statusKey}`] || '',
					isSaving ? heroS.statusBannerSaving : '',
				]
					.filter(Boolean)
					.join(' ')}
				onClick={onToggle}
				disabled={isSaving}
				aria-haspopup="listbox"
				aria-expanded={isOpen}
			>
				<StatusProgressIcon
					status={statusKey}
					size={14}
					className={heroS.statusIcon}
					ringColor="var(--color-white)"
				/>
				<span>{statusLabel}</span>
			</button>
			{menu}
		</>
	)
}

export default ApplicationStatusPicker
